import { useEffect, useState } from 'react';
import { getFilasPlanillaTramite } from '../../services/tramiteService';
import { getFilasPlanillaCliente } from '../../services/clienteService';

const VERDE = '#15803d';
const caja = { border: '1px solid #bbf7d0', background: '#f0fdf4', borderRadius: 10, padding: '8px 12px' };
const enlace = { fontSize: '0.85rem', color: VERDE, fontWeight: 600, background: 'none', border: 'none', padding: 0, cursor: 'pointer' };

/** "LIQUIDAR, fila 312" — o "LIQUIDAR, fila 312 · TURNOS ANSES, fila 40" si son varias. */
const origen = (filas) => filas.map((f) => `${f.hoja}, fila ${f.nroFila}`).join(' · ');

/**
 * Las filas de planilla de un trámite. Se trae con una promesa que atrapa todo: si el servicio no
 * está (pruebas con el módulo mockeado) la ficha no se rompe.
 */
function useFilas(cargar, clave) {
    const [filas, setFilas] = useState([]);
    useEffect(() => {
        let vigente = true;
        setFilas([]);
        Promise.resolve()
            .then(cargar)
            .then((datos) => vigente && setFilas(Array.isArray(datos) ? datos : []))
            .catch(() => vigente && setFilas([]));
        return () => { vigente = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clave]);
    return filas;
}

/**
 * Las celdas de una fila de la planilla, con el título de su columna.
 *
 * Es la constancia de origen de la migración del Excel: lo que decía la planilla se lee acá, sin
 * abrir el archivo. Las columnas que venían vacías no se muestran.
 */
export function TablaFilaPlanilla({ fila }) {
    if (!fila) return null;
    return (
        <div>
            <div style={{ fontSize: '0.8rem', color: '#4b5563', marginBottom: 6 }}>
                <i className="bi bi-file-earmark-excel me-1" style={{ color: VERDE }}></i>
                <strong>{fila.hoja}</strong>, fila {fila.nroFila}
                {fila.archivo && <> · {fila.archivo}</>}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(110px, max-content) minmax(0, 1fr)', gap: '4px 12px' }}>
                {fila.columnas.map((c) => (
                    <div key={c.titulo} style={{ display: 'contents' }}>
                        <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151' }}>{c.titulo}</span>
                        <span style={{ fontSize: '0.85rem', color: '#111827', whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>
                            {c.valor}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function ListaFilas({ filas, conTramite = false }) {
    return filas.map((f, i) => (
        <div key={`${f.idTramite}-${f.hoja}-${f.nroFila}`} className="mt-2 pt-2"
             style={i > 0 || conTramite ? { borderTop: '1px solid #bbf7d0' } : undefined}>
            {conTramite && <div style={{ fontSize: '0.8rem', color: '#374151', marginBottom: 4 }}>Trámite #{f.idTramite}</div>}
            <TablaFilaPlanilla fila={f} />
        </div>
    ));
}

/**
 * En la ficha del trámite: "Proviene de migración (LIQUIDAR, fila N)", con las filas del Excel
 * plegadas detrás. Si el trámite se cargó en el sistema, no dibuja nada.
 */
export function TarjetaFilaPlanilla({ idTramite }) {
    const filas = useFilas(() => getFilasPlanillaTramite(idTramite), idTramite);
    const [abierta, setAbierta] = useState(false);
    useEffect(() => setAbierta(false), [idTramite]);

    if (filas.length === 0) return null;
    return (
        <div style={{ ...caja, minWidth: 0, flex: abierta ? '1 1 100%' : '0 0 auto' }}>
            <button type="button" onClick={() => setAbierta(!abierta)} style={enlace}>
                <i className="bi bi-file-earmark-excel me-1" />
                {abierta ? 'Ocultar los datos del Excel' : `Proviene de migración (${origen(filas)})`}
            </button>
            {abierta && <ListaFilas filas={filas} />}
        </div>
    );
}

/** En la ficha del cliente: las filas del Excel de las que salieron sus trámites. */
export function FilasPlanillaCliente({ cuil }) {
    const filas = useFilas(() => getFilasPlanillaCliente(cuil), cuil);
    const [abierta, setAbierta] = useState(false);
    useEffect(() => setAbierta(false), [cuil]);

    if (filas.length === 0) return null;
    return (
        <div style={{ ...caja, margin: '0 0 12px' }}>
            <button type="button" onClick={() => setAbierta(!abierta)} style={enlace}>
                <i className="bi bi-file-earmark-excel me-1" />
                {abierta ? 'Ocultar los datos del Excel' : `Proviene de migración (${origen(filas)})`}
            </button>
            {abierta && <ListaFilas filas={filas} conTramite />}
        </div>
    );
}

/** Desplegada debajo de la fila del listado A liquidar. */
export default function FilaPlanilla({ idTramite }) {
    const [estado, setEstado] = useState('cargando');
    const [filas, setFilas] = useState([]);

    useEffect(() => {
        let vigente = true;
        setEstado('cargando');
        getFilasPlanillaTramite(idTramite)
            .then((datos) => {
                if (!vigente) return;
                setFilas(datos);
                setEstado(datos.length ? 'listo' : 'sin-fila');
            })
            .catch(() => vigente && setEstado('error'));
        return () => { vigente = false; };
    }, [idTramite]);

    if (estado === 'cargando') return <span style={{ fontSize: '0.85rem', color: '#4b5563' }}>Cargando los datos del Excel…</span>;
    if (estado === 'error') return <span style={{ fontSize: '0.85rem', color: '#b91c1c' }}>No se pudieron traer los datos del Excel.</span>;
    if (estado === 'sin-fila') return <span style={{ fontSize: '0.85rem', color: '#4b5563' }}>Este trámite no proviene de migración.</span>;
    return <ListaFilas filas={filas} />;
}
